import Select from "components/form/select";
import Submit from "components/submit";
import Closable from "components/behaviors/closable";

import { FiX } from "react-icons/fi";
import { Filters as FiltersType } from "types/filters";
import { Pop } from "transitions/pop";
import clsx from "clsx";

import { useState } from "react";
import { useTopics } from "hooks/metadata";
import { useNotifications } from "hooks/notifications";
import { track } from "utils/tracker";
import { useMounted } from "hooks/react";

interface FiltersProps {
  actionText: string;
  isClosable?: boolean;
  className: string;
  loading?: boolean;
  onSearch: (filters: any) => void;
  onChange?: (filters: any) => void;
  onClose: () => void;
  children?: React.ReactNode;
}

export default function Filters(props: FiltersProps): JSX.Element {
  const { actionText } = props;
  const { isClosable, className } = props;
  const { onChange, onSearch } = props;

  const initialFilters: FiltersType = { language: "ingles" };
  const [filters, setFilters] = useState(initialFilters);
  const [topics, subTopics] = useTopics(filters.subject);
  const [visible, setVisible] = useState(true);

  useMounted();

  const transitionDuration = 200;

  const { setNotification } = useNotifications();

  function handleSelect({ name, value }) {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    onChange && onChange(newFilters);
  }

  function handleSearch() {
    if (filters.seriousFrom) track("Selected test mode");
    onSearch(filters);
  }

  function handleClose() {
    if (visible) setVisible(false);
    setTimeout(props.onClose, transitionDuration);
  }

  function topicOptions(topics) {
    return topics.map((topic) => {
      const { name, slug } = topic;
      return { label: name, value: { name, slug } };
    });
  }

  function selectInput(config) {
    const { label, name, disabled, nullLabel, callback } = config;
    let options = config.options || [];
    if (nullLabel) options = [{ label: nullLabel, value: null }, ...options];

    return (
      <Select
        onSelect={handleSelect}
        callback={callback}
        label={label}
        name={name}
        value={filters[name] || null}
        options={options}
        disabled={disabled}
        onClickDisabled={config.onClickDisabled}
        className="mb-4"
      />
    );
  }

  function clearSubTopic() {
    setFilters({ ...filters, topic: null });
  }

  function notifyTopicDisabledClick() {
    setNotification({
      title: "O campo está bloqueado",
      text: "Selecione uma disciplina primeiro"
    });
  }

  return (
    <>
      <Pop
        appear={isClosable}
        visible={visible}
        timeout={transitionDuration}
        classNames="pop"
      >
        <div
          className={clsx(
            "questionnaire-filters relative w-full h-full bg-white overflow-y-scroll",
            className
          )}
        >
          <Closable onClose={handleClose} skip={() => !isClosable}>
            <div className="p-5">
              <span
                className={clsx(
                  "absolute right-5 top-5 text-emphasis-low hover:text-emphasis-medium cursor-pointer transition-colors",
                  { hidden: !isClosable }
                )}
                onClick={handleClose}
              >
                <FiX size={20} />
              </span>
              <div className="mb-6">
                <span className="block mb-4 text-lg">Escolha por conteúdo</span>
                {selectInput({
                  label: "Disciplina",
                  name: "subject",
                  options: topicOptions(topics),
                  disabled: JSON.stringify(topics) === "[]",
                  nullLabel: "Todas",
                  callback: clearSubTopic
                })}
                {selectInput({
                  label: "Tópico",
                  name: "topic",
                  options: topicOptions(subTopics),
                  disabled: !filters.subject || JSON.stringify(topics) === "[]",
                  nullLabel: "Todos",
                  onClickDisabled: notifyTopicDisabledClick
                })}
              </div>
              {props.children}
              <div className="md:hidden">
                <Submit
                  onClick={handleSearch}
                  loading={props.loading}
                  className="btn btn-primary w-full"
                >
                  {actionText}
                </Submit>
              </div>
              <div className="hidden md:block">
                <Submit
                  onClick={handleSearch}
                  loading={props.loading}
                  className="btn btn-secondary w-full"
                >
                  {actionText}
                </Submit>
              </div>
            </div>
          </Closable>
        </div>
      </Pop>
    </>
  );
}
